import { Component, type ErrorInfo, type ReactNode } from "react";
import { C, FONT, card } from "../theme";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Catches render-time crashes in the routed view area and shows an on-brand fallback,
 *  so a single bad render can't blank the whole dashboard. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface in the console for diagnosis; the dashboard keeps polling regardless.
    console.error("Dashboard render error:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          ...card,
          maxWidth: 560,
          margin: "24px auto",
          padding: "26px 28px",
          background: C.raise,
        }}
      >
        <div
          className="serif"
          style={{ fontFamily: FONT.serif, fontSize: 22, fontWeight: 500, color: C.ink, lineHeight: 1.15 }}
        >
          Something went wrong
        </div>
        <div style={{ marginTop: 8, fontSize: 13.5, color: C.textMid, lineHeight: 1.5 }}>
          This view hit a snag. The dashboard keeps talking to the controller and will retry on the
          next refresh.
        </div>
        <details
          style={{
            marginTop: 16,
            background: C.paper,
            border: `1px solid ${C.line}`,
            borderRadius: 12,
            padding: "10px 14px",
          }}
        >
          <summary
            style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: C.textMid }}
          >
            Error details
          </summary>
          <pre
            className="mono"
            style={{
              margin: "10px 0 0",
              fontFamily: FONT.mono,
              fontSize: 11.5,
              color: C.textLow,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {error.message || String(error)}
          </pre>
        </details>
      </div>
    );
  }
}
